import { SupportedTokenName } from "@mezo/sdk"
import { createSlice } from "@reduxjs/toolkit"

import { BITCOIN_MIN_DEPOSIT, LOCK_TIMES } from "#/shared/constants"
import { Flow, LockTime } from "#/shared/types"

type DepositStatus = "idle" | "pending" | "completed"

type TokenApprovalStatus = "approved" | "pending" | "non-approved"

export type DepositState = {
  depositAmount: string
  lockTime: LockTime
  activeToken: SupportedTokenName
  depositStatus: DepositStatus
  depositModalClosed: boolean
  tokenApprovalStatus: TokenApprovalStatus
  depositId: number | null
  hasDepositError: boolean
  hasApprovalError: boolean
}

const initialState: DepositState = {
  depositAmount: "",
  lockTime: LOCK_TIMES[0],
  activeToken: "BTC",
  depositStatus: "idle",
  depositModalClosed: false,
  tokenApprovalStatus: "non-approved",
  depositId: null,
  hasDepositError: false,
  hasApprovalError: false,
}

/**
 * TODO: This slice should be renamed to something like "transactionSlice"
 * since right now it covers more actions that just deposit
 */
const depositSlice = createSlice({
  name: "deposit",
  initialState,
  reducers: {
    setDepositAmount: (immerState, { payload }: { payload: string }) => {
      immerState.depositAmount = payload
    },
    setActiveToken: (
      immerState,
      { payload }: { payload: SupportedTokenName },
    ) => {
      immerState.activeToken = payload
    },
    setDepositStatus: (immerState, { payload }: { payload: DepositStatus }) => {
      immerState.depositStatus = payload
    },
    setTokenApprovalStatus: (
      immerState,
      { payload }: { payload: TokenApprovalStatus },
    ) => {
      immerState.tokenApprovalStatus = payload
    },
    setDepositId: (immerState, { payload }: { payload: number | null }) => {
      immerState.depositId = payload
    },
    setHasDepositError: (immerState, { payload }: { payload: boolean }) => {
      immerState.hasDepositError = payload
    },
    setHasApprovalError: (immerState, { payload }: { payload: boolean }) => {
      immerState.hasApprovalError = payload
    },
    resetErrorState: (immerState) => {
      immerState.hasDepositError = initialState.hasDepositError
      immerState.hasApprovalError = initialState.hasApprovalError
    },
    resetDepositState: (
      immerState,
      {
        payload,
      }: {
        payload: {
          flow: Flow
          initialDepositAmount?: string
          resetDepositId?: boolean
        }
      },
    ) => {
      // This will allow to use no default value when we pass empty string
      const shouldUseInitialDepositAmount =
        !!payload.initialDepositAmount || payload.initialDepositAmount === ""

      const defaultDepositAmount =
        payload.flow === "ethereum" ? BITCOIN_MIN_DEPOSIT.toString() : ""

      return {
        ...initialState,
        depositAmount: shouldUseInitialDepositAmount
          ? payload.initialDepositAmount!
          : defaultDepositAmount,
        depositId: payload.resetDepositId ? null : immerState.depositId,
      }
    },
  },
})

export const {
  setDepositAmount,
  setActiveToken,
  setDepositStatus,
  setTokenApprovalStatus,
  setDepositId,
  setHasDepositError,
  setHasApprovalError,
  resetErrorState,
  resetDepositState,
} = depositSlice.actions

export default depositSlice.reducer
